"use client";

import { SentryFeatureTags, logException } from "@punchup/sentry";
import { VenuePagesRow, updateVenueDescription } from "../../data/venue_pages";
import { startTransition, useState } from "react";

import { VenueDescriptionForm } from "./dashboard/venue-manager/settings/VenueDescriptionForm";
import { cn } from "@punchup/utils/cn";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { revalidatePathClient } from "@punchup/utils/vercel";
import { useRouter } from "next/navigation";
import { useToast } from "@punchup/ui-common/shadcn/ui/use-toast";

export default function VenueDescription({
  canEdit,
  venuePage,
}: {
  canEdit?: boolean;
  venuePage: VenuePagesRow;
}) {
  const { toast } = useToast();
  const router = useRouter();
  const supabase = createClientComponentClient();
  const [isEditing, setIsEditing] = useState(false);

  const { description } = venuePage;

  const handleUpdateDescription = async (newDescription: string) => {
    try {
      await updateVenueDescription({
        supabase,
        venuePageId: venuePage.id,
        newDescription,
      });

      await revalidatePathClient(`/v/${venuePage.slug}`);
      startTransition(() => {
        // Refresh the current route and fetch new data from the server without
        // losing client-side browser or React state.
        router.refresh();
      });
      setIsEditing(false);
      toast({
        title: "Success",
        variant: "success",
        description: "Venue description updated successfully",
      });
    } catch (error) {
      logException(error, {
        extra: {
          venuePageId: venuePage.id,
        },
        tags: {
          feature: SentryFeatureTags.Venues,
        },
      });
      toast({
        title: "Error",
        description: "Failed to update venue description. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (!description && !canEdit) return null;

  // Settings page layout (canEdit mode)
  if (canEdit) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-6">
          {!description && (
            <span className="text-base text-secondary-blue">
              Use the Edit button to add a description for your venue. It will
              appear on your page.
            </span>
          )}
          <VenueDescriptionForm
            description={description}
            isEditing={isEditing}
            onEdit={() => setIsEditing(true)}
            onCancel={() => setIsEditing(false)}
            onSubmit={async (values) => {
              await handleUpdateDescription(values.description?.trim() || "");
            }}
          />
        </div>
      </div>
    );
  }

  // Public venue page layout
  return (
    <div className="flex flex-col gap-y-4">
      <h4 className="text-lg text-primary-blue font-bold">About</h4>
      <div className={cn("grid gap-x-2 gap-y-8")}>
        <div className="relative group">
          <div className="flex gap-x-2 items-start">
            <div className="flex flex-col w-full">
              <div className="text-base text-secondary-blue whitespace-pre-wrap">
                {description}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
